import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  AlertDescription,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@roger/ui-components';
import { AlertTriangle } from 'lucide-react';
import {
  MembersTable,
  useClinicianManager,
  type OrgMemberResponse,
} from '../components/clinician-manager';
import {
  deactivateUser,
  retryDeactivateUser,
  onboardUsers,
  reactivateAgencyMember,
  resendInvite,
  rescindInvite,
} from '../services/api';
import {
  getBusinessErrorMessage,
  getSurfaceableError,
} from '../utils/apiErrorDetail';
import { getHttpStatus } from '../utils/httpErrorUtils';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';
import { MemberSettingsDialog } from '../components/rbac/role-assignment-dialog/MemberSettingsDialog';
import {
  OnboardedConfirmation,
  OnboardUserDialog,
  countMembersByArchetype,
  onboardedMessage,
  type OnboardUserSubmission,
} from '../components/clinician-manager/onboarding';

// ─── Component ──────────────────────────────────────────────────────────────

const ClinicianManager: React.FC = () => {
  const cm = useClinicianManager();
  const { user, showSuccess, showError, hasPermission } = useStore(
    useShallow(state => ({
      user: state.user,
      showSuccess: state.showSuccess,
      showError: state.showError,
      hasPermission: state.hasPermission,
    }))
  );
  const canInvite = hasPermission('clinician-mgr:invite-users');
  // Creating or linking a Roger App account needs the clinician-manager
  // permission, not just the invite one, so the section is offered only to
  // operators the endpoint will actually accept it from.
  const canCreateRogerDocAccounts = hasPermission('clinician-mgr:access');
  // Its own permission: taking somebody's access to the agency away is not the
  // same authority as administering them. Ships dark, so nobody sees the
  // control until the slug is granted.
  const canDeactivate = hasPermission('users:deactivate');
  // Retracting an invitation is the inverse of sending one, so it rides the
  // same permission rather than a new one.
  const canCancelInvite = hasPermission('clinician-mgr:invite-users');

  // Runs after the invite so accounts are only provisioned for users the invite
  // actually created. RogerDoc emails each new account a link to set their
  // password; a failure here leaves the invitation itself intact.

  const [isOnboarding, setIsOnboarding] = useState(false);
  /** Set once somebody is added, to say what happens next. */
  const [onboarded, setOnboarded] = useState<{
    name: string;
    email: string;
    appUsername: string | null;
    portalInvited: boolean;
    appAccountCreated: boolean;
    appSetupEmailSent: boolean | null;
    hasAppAccount: boolean;
  } | null>(null);

  const handleOnboard = useCallback(
    async (submission: OnboardUserSubmission) => {
      setIsOnboarding(true);
      try {
        const res = await onboardUsers({
          body: { users: [submission] },
          // Without this a 422 resolves rather than throws, so the refusal
          // reaches the catch as "No response data" and the server's reason --
          // which names the thing to fix -- is replaced by a generic retry.
          throwOnError: true,
        });
        const result = res.data?.results?.[0];
        if (!result) throw new Error('No response data');

        const name =
          [submission.first_name, submission.last_name]
            .filter(Boolean)
            .join(' ') || submission.email;

        // Only an invitation puts an email in their inbox. Somebody who already
        // had a Roger login is added to the organisation instead and hears
        // nothing, so saying "invited" would have them waiting for mail that was
        // never sent.
        const added = onboardedMessage({
          name,
          portalInvited: result.portal_invited === true,
        });

        // The Roger records are committed by the time we get here, so a failure
        // in one of the external steps is reported rather than treated as the
        // whole thing having failed. A refusal never reaches here: the endpoint
        // turns a single-person failure into a 422, handled in the catch below.
        if (result.workos === 'failed') {
          showError(
            `${name} was added, but the invitation email could not be sent. Use Resend invite.`
          );
        } else if (
          result.roger_doc === 'failed' ||
          result.roger_doc === 'conflict'
        ) {
          showError(
            `${added}, but their Roger App account was not created. ${result.reason ?? ''}`
          );
        } else if (result.roger === 'partial') {
          showSuccess(
            added,
            'Some agency roles were unavailable — check their member settings.'
          );
        }

        // On the clean path the confirmation carries the outcome, so a toast
        // saying the same thing would be noise. The branches above are the ones
        // where something needs saying that the confirmation does not cover.
        if (
          result.workos !== 'failed' &&
          result.roger_doc !== 'failed' &&
          result.roger_doc !== 'conflict'
        ) {
          setOnboarded({
            name,
            email: result.email,
            appUsername: result.roger_doc_username ?? null,
            // Both of these are about which emails actually got sent, not about
            // whether the step succeeded. Somebody who already had a Roger login
            // is added to the agency without an invitation, and linking an app
            // account sends no setup directions -- reporting either as "expect an
            // email" sends them hunting for something nobody sent.
            portalInvited: result.portal_invited === true,
            appAccountCreated: result.roger_doc_created === true,
            // Distinct from the line above: that one says an account was made,
            // this one says the email carrying its password actually went out.
            appSetupEmailSent: result.roger_doc_setup_email_sent ?? null,
            hasAppAccount: result.roger_doc === 'ok',
          });
        }
        cm.closeInviteDialog();
        cm.loadData();
        cm.loadRogerDocLinks();
      } catch (err) {
        console.error('Failed to onboard user:', err);
        // The refusal is authored for the operator and flagged surfaceable, so
        // prefer it; the generic line is for a genuine transport failure.
        showError(
          getSurfaceableError(err)?.message ??
            getBusinessErrorMessage(err) ??
            'Failed to add this person. Please try again.'
        );
      } finally {
        setIsOnboarding(false);
      }
    },
    [
      showSuccess,
      showError,
      cm.closeInviteDialog,
      cm.loadData,
      cm.loadRogerDocLinks,
    ]
  );

  const archetypeCounts = useMemo(
    () => countMembersByArchetype(cm.members),
    [cm.members]
  );

  // The member list already loads every Roger App account in the agency, so the
  // dialog can spot an existing one before the operator submits rather than
  // after the server refuses.
  const existingAppAccounts = useMemo(
    () =>
      cm.rogerDocAccounts.map(account => ({
        id: account.id,
        username: account.username,
        first_name: account.first_name,
        last_name: account.last_name,
        linkedToEmail: account.roger_emr?.user_email ?? null,
      })),
    [cm.rogerDocAccounts]
  );

  const resendInviteTo = useCallback(
    async (email: string): Promise<boolean> => {
      try {
        // The API client resolves (rather than throws) on HTTP errors, so an
        // absent `data` is how we detect a failed resend.
        const res = await resendInvite({
          body: { email },
        });
        if (!res.data) throw new Error('No response data');
        showSuccess(`Invitation resent to ${email}`);
        return true;
      } catch (err) {
        console.error('Failed to resend invitation:', err);
        showError('Failed to resend invitation. Please try again.');
        return false;
      }
    },
    [showSuccess, showError]
  );

  // Deactivating locks somebody out, so it is confirmed. Reactivating only
  // gives access back, so it is immediate.
  const [memberToDeactivate, setMemberToDeactivate] =
    useState<OrgMemberResponse | null>(null);
  const [isDeactivating, setIsDeactivating] = useState(false);

  const handleToggleActivation = useCallback(
    async (member: OrgMemberResponse) => {
      if (!member.roger_user_id) return;
      if (member.deactivated_at) {
        try {
          await reactivateAgencyMember({
            path: { user_id: member.roger_user_id },
            throwOnError: true,
          });
          showSuccess('Access restored.');
          cm.loadData();
        } catch (err) {
          console.error('Failed to reactivate member:', err);
          // The route can refuse on the clinic bound, and "try again" is the
          // wrong advice for that, so the server's reason wins when it gives one.
          showError(
            getBusinessErrorMessage(err) ??
              'Failed to restore access. Please try again.'
          );
        }
        return;
      }
      setMemberToDeactivate(member);
    },
    [cm.loadData, showSuccess, showError]
  );

  const confirmDeactivation = useCallback(async () => {
    const member = memberToDeactivate;
    if (!member?.roger_user_id) return;
    setIsDeactivating(true);
    try {
      await deactivateUser({
        path: { user_id: member.roger_user_id },
        throwOnError: true,
      });
      showSuccess('Access to this agency removed.');
      setMemberToDeactivate(null);
      cm.loadData();
    } catch (err) {
      console.error('Failed to deactivate member:', err);
      showError(
        getBusinessErrorMessage(err) ??
          (getHttpStatus(err) === 502
            ? 'Access was removed here, but directory removal needs a retry.'
            : 'Failed to remove access. Please try again.')
      );
      // The backend records a pending WorkOS removal before reporting an
      // external failure, so refresh to expose the retry action immediately.
      cm.loadData();
    } finally {
      setIsDeactivating(false);
    }
  }, [memberToDeactivate, cm.loadData, showSuccess, showError]);

  const retryDeactivation = useCallback(
    async (member: OrgMemberResponse) => {
      if (!member.roger_user_id) return;
      try {
        await retryDeactivateUser({
          path: { user_id: member.roger_user_id },
          throwOnError: true,
        });
        showSuccess('Access removal retried.');
      } catch (err) {
        console.error('Failed to retry deactivation:', err);
        showError(
          getBusinessErrorMessage(err) ??
            'Failed to retry access removal. Please try again.'
        );
      } finally {
        cm.loadData();
      }
    },
    [cm.loadData, showSuccess, showError]
  );

  // Irreversible: the invitation is revoked and the rows the invite created go
  // with it, so re-inviting starts over. Always confirmed.
  const [inviteToCancel, setInviteToCancel] =
    useState<OrgMemberResponse | null>(null);
  const [isCancellingInvite, setIsCancellingInvite] = useState(false);

  const handleCancelInvite = useCallback(async (member: OrgMemberResponse) => {
    setInviteToCancel(member);
  }, []);

  const confirmCancelInvite = useCallback(async () => {
    const member = inviteToCancel;
    if (!member?.roger_user_id) return;
    setIsCancellingInvite(true);
    try {
      await rescindInvite({
        body: { email: member.email },
        throwOnError: true,
      });
      showSuccess('Invitation cancelled.');
      setInviteToCancel(null);
      cm.loadData();
    } catch (err) {
      console.error('Failed to cancel the invitation:', err);
      // The route explains a refusal it can only answer with a reason, so show
      // that rather than telling them to try again at something that will not
      // work a second time.
      showError(
        getBusinessErrorMessage(err) ??
          'Failed to cancel the invitation. Please try again.'
      );
    } finally {
      setIsCancellingInvite(false);
    }
  }, [inviteToCancel, cm.loadData, showSuccess, showError]);

  const handleResendInvite = useCallback(
    // The table's callback wants void; the confirmation wants the outcome, so it
    // calls resendInviteTo directly.
    async (member: OrgMemberResponse) => {
      await resendInviteTo(member.email);
    },
    [resendInviteTo]
  );

  // ── Permission gate ──

  if (!cm.hasAccess) {
    return (
      <div className='container mx-auto px-4 py-6'>
        <Alert className='border-red-200 bg-red-50'>
          <AlertDescription className='text-red-800 flex gap-2 items-center'>
            <AlertTriangle className='h-4 w-4 text-red-600' />
            You don&apos;t have permission to access this page. Please contact
            your administrator if you need access.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // ── Render ──

  return (
    <div className='space-y-6'>
      {/* Members table */}
      <MembersTable
        members={cm.filteredMembers}
        isLoading={cm.isLoading}
        hasSearchQuery={!!cm.searchQuery}
        searchQuery={cm.searchQuery}
        onSearchChange={cm.setSearchQuery}
        onEditMember={cm.openEditDialog}
        onInvite={cm.openInviteDialog}
        onResendInvite={handleResendInvite}
        canInvite={canInvite}
        canDeactivate={canDeactivate}
        onToggleActivation={handleToggleActivation}
        onRetryDeactivation={retryDeactivation}
        currentUserId={user?.id}
        canCancelInvite={canCancelInvite}
        onCancelInvite={handleCancelInvite}
        rogerDocLinksByEmail={cm.rogerDocLinksByEmail}
        rogerDocAccountsLoaded={cm.rogerDocAccountsLoaded}
        hiddenClinicIds={cm.kinnserClinicIds}
        clinicNamesById={cm.clinicNamesById}
      />

      {/* Member settings dialog (name editing + role assignment) */}
      <MemberSettingsDialog
        clinics={cm.roleAssignableClinics}
        preserveRoleAssignmentsForClinicIds={cm.kinnserClinicIds}
        member={cm.editingMember}
        onClose={cm.closeEditDialog}
        onSuccess={() => {
          cm.closeEditDialog();
          cm.loadData();
        }}
        onRogerDocLinkChanged={cm.loadRogerDocLinks}
      />

      <Dialog
        open={!!inviteToCancel}
        onOpenChange={open => {
          if (!open && !isCancellingInvite) setInviteToCancel(null);
        }}
      >
        <DialogContent className='sm:max-w-[480px]'>
          <DialogHeader>
            <DialogTitle>Cancel this invitation?</DialogTitle>
            <DialogDescription>
              The invitation to {inviteToCancel?.email} will be revoked, so the
              link in their email stops working, and the access it granted is
              removed. They disappear from this list. This cannot be undone:
              bringing them in later means inviting them again.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type='button'
              variant='outline'
              onClick={() => setInviteToCancel(null)}
              disabled={isCancellingInvite}
            >
              Keep invitation
            </Button>
            <Button
              type='button'
              onClick={confirmCancelInvite}
              disabled={isCancellingInvite}
            >
              Cancel invitation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!memberToDeactivate}
        onOpenChange={open => {
          if (!open && !isDeactivating) setMemberToDeactivate(null);
        }}
      >
        <DialogContent className='sm:max-w-[480px]'>
          <DialogHeader>
            <DialogTitle>Remove access to this agency?</DialogTitle>
            <DialogDescription>
              {[memberToDeactivate?.first_name, memberToDeactivate?.last_name]
                .filter(Boolean)
                .join(' ') || memberToDeactivate?.email}{' '}
              will not be able to sign in to this agency, and will stop
              appearing in assignment and scheduling lists. Their history stays,
              and so do their roles and clinic access, so you can restore them
              here at any time. Access to any other agency is unaffected.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type='button'
              variant='outline'
              onClick={() => setMemberToDeactivate(null)}
              disabled={isDeactivating}
            >
              Cancel
            </Button>
            <Button
              type='button'
              onClick={confirmDeactivation}
              disabled={isDeactivating}
            >
              Remove access
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add a team member -- everything they need, in one pass. */}
      <OnboardUserDialog
        open={cm.showInviteDialog}
        allowRogerDocAccounts={canCreateRogerDocAccounts}
        clinics={cm.roleAssignableClinics}
        agencyAbbreviation={user?.agency?.abbreviation}
        existingAppAccounts={existingAppAccounts}
        archetypeCounts={archetypeCounts}
        isSubmitting={isOnboarding}
        onClose={cm.closeInviteDialog}
        onSubmit={handleOnboard}
      />

      {onboarded && (
        <OnboardedConfirmation
          open
          name={onboarded.name}
          email={onboarded.email}
          appUsername={onboarded.appUsername}
          portalInvited={onboarded.portalInvited}
          appAccountCreated={onboarded.appAccountCreated}
          appSetupEmailSent={onboarded.appSetupEmailSent}
          hasAppAccount={onboarded.hasAppAccount}
          onClose={() => setOnboarded(null)}
          onResendInvite={() => resendInviteTo(onboarded.email)}
        />
      )}
    </div>
  );
};

export default ClinicianManager;
